/* eslint-disable react-hooks/exhaustive-deps */
import clsx from 'clsx'
import {cloneDeep, get, isEmpty} from 'lodash'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {PackageType, ParcelItem} from './PackageType'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import OrderHelper from '../../../utils/order'
import {KTSVG} from '../../../../../../gori/components'
import {useStatesGlobal} from '../../../../../../gori/providers'
import {Button, InputTextFormik, SelectFormik} from '../../../../../../gori/widgets'
import {
  DIMENSION_UNIT,
  OPTION_WEIGHT_UNIT,
  WEIGHT_UNIT,
  DIMENSIONS_PLACEHOLDER,
  DIMENSIONS,
  OPTION_PACKAGE_TYPE,
  UNIT_VALUES,
  OPTION_DIMENSION_UNIT,
} from '../../../../../../gori/constants'
import {convertUnit, round} from '../../../../../../gori/helpers'
import {getCSSVariableValue} from '../../../../../../gori/assets/ts/_utils'

type Props = {
  formik: any
  disabled?: boolean
  className?: string
  hasGetPackage?: boolean
  handleAddNewItem?: any
  handleDeleteItem?: any
  currentDimensionUnit?: string
  setCurrentDimensionUnit?: any
  currentWeightUnit?: string
  setCurrentWeightUnit?: any
}

const PackageInfo: React.FC<Props> = ({
  formik,
  className = '',
  disabled,
  hasGetPackage = true,
  handleAddNewItem,
  handleDeleteItem,
  currentDimensionUnit,
  setCurrentDimensionUnit,
  currentWeightUnit,
  setCurrentWeightUnit,
}) => {
  const intl = useIntl()
  const {statesGlobal} = useStatesGlobal()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [reachLimitRow, setReachLimitRow] = useState(false)
  const packageTypePlaceholder = intl.formatMessage({id: 'PACKAGE_TYPE'})
  const [labelPackageType, setLabelPackageType] = useState<string | undefined>(
    packageTypePlaceholder
  )
  const [isDisabledPackageCount, setIsDisabledPackageCount] = useState(false)

  const carriersPackage = statesGlobal?.settingsConfig?.package_types || []

  const isDisabledDimension = useMemo(() => {
    if (disabled) return true

    if (Object.values(formik.values.parcels).length > 1) return false

    if (labelPackageType === packageTypePlaceholder) return false

    const currentParcels = formik.values.parcels || {}
    const firstParcelKey = Object.keys(currentParcels)?.[0]
    const firstParcelValue = currentParcels?.[firstParcelKey]
    if (firstParcelValue.package_id) return false

    return !(
      labelPackageType === OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label ||
      labelPackageType === OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label ||
      labelPackageType?.toLowerCase().includes('cubic')
    )
  }, [disabled, labelPackageType, Object.values(formik.values.parcels).length])

  useEffect(() => {
    const currentParcels = formik.values.parcels || {}
    const firstParcelKey = Object.keys(currentParcels)?.[0]
    const firstParcelValue = currentParcels?.[firstParcelKey]

    let disableFlag =
      isDisabledDimension ||
      ((labelPackageType?.toLowerCase().includes('cubic') && firstParcelValue.package_type) ??
        false)

    setIsDisabledPackageCount(disableFlag)
  }, [formik.values.parcels, labelPackageType])
  const baseColor = getCSSVariableValue('--primary')

  const customSelectInputStyle = useCallback((disabled) => {
    const color = disabled ? '#a1a5b7' : baseColor
    return {
      styleInput: {
        background: 'transparent',
        width: '80px',
        backgroundImage: 'none',
        borderColor: 'transparent',
        '&:hover': {
          borderColor: 'transparent',
          background: '#F0FDFA',
        },
        color,
        textAlign: 'right',
      },
      styleDropdownIndicator: {
        color,
        '&:hover': {
          color,
        },
        padding: 0,
        marginRight: 0,
      },
      customMenuPortal: {
        marginTop: '-14px',
      },
    }
  }, [])

  // Set default weight_unit for all parcels on mount
  useEffect(() => {
    const parcels = formik.getFieldProps('parcels').value

    const firstParcel = Object.values(parcels)?.[0] as ParcelItem

    if (firstParcel) {
      const weightUnit = firstParcel.weight_unit || WEIGHT_UNIT.OZ
      const dimensionUnit = firstParcel.dimension_unit || DIMENSION_UNIT.IN

      // Set currentWeightUnit and currentDimensionUnit based on first parcel
      setCurrentWeightUnit(weightUnit)
      setCurrentDimensionUnit(dimensionUnit)

      // Set initial values for formik
      formik.setFieldValue('weight_unit', weightUnit)
      formik.setFieldValue('dimension_unit', dimensionUnit)

      if (!firstParcel.weight_unit || !firstParcel.dimension_unit) {
        // Set weight_unit and dimension_unit for all parcels
        Object.keys(parcels).forEach((key) => {
          formik.setFieldValue(`parcels.${key}.weight_unit`, weightUnit)
          formik.setFieldValue(`parcels.${key}.dimension_unit`, dimensionUnit)
        })
      }
    }
  }, [formik.getFieldProps('parcels').value])

  // BEGIN: validate Total Weight
  const totalWeightPackage = useMemo(() => {
    let sum = 0
    Object.entries(formik.values.parcels).forEach(([key, value]: [any, any]) => {
      const weightItem = Number.parseFloat(value?.weight) || 0
      const packageCountItem = Number.parseFloat(value?.package_count) || 1
      sum += weightItem * packageCountItem
    })
    return round(sum)
  }, [formik.values.parcels])

  const totalWeightCustoms = useMemo(() => {
    let sum = 0
    Object.entries(formik?.values?.custom_items || {}).forEach(([key, value]: [any, any]) => {
      sum += Number.parseFloat(value?.weight) || 0
    })
    return round(sum)
  }, [formik.values.custom_items])
  useEffect(() => {
    if (!formik.values.custom_items || !Object.keys(formik.values.parcels).length) return

    const weight_unit = get(Object.values(formik.values.parcels), '[0].weight_unit', '')

    if (!weight_unit) return

    const valueConvert = convertUnit(
      {unit: WEIGHT_UNIT.OZ, value: totalWeightCustoms},
      {unit: weight_unit}
    )

    if (valueConvert > totalWeightPackage) {
      formik.setFieldValue('check_weight', true)
    } else {
      formik.setFieldValue('check_weight', false)
    }
  }, [formik.values.parcels, formik.values.custom_items, totalWeightCustoms, totalWeightPackage])
  // END: validate Total Weight

  // BEGIN: validate Total Package Count
  const totalPackageCount = useMemo(() => {
    let sum = 0
    Object.entries(formik.values.parcels).forEach(([key, value]: [any, any]) => {
      const packageCountItem = Number.parseFloat(value?.package_count) || 1
      sum += packageCountItem
    })
    return sum
  }, [formik.values.parcels])

  useEffect(() => {
    if (totalPackageCount > UNIT_VALUES.PACKAGE_COUNT.max) {
      formik.setFieldValue('check_package_count', true)
    } else {
      formik.setFieldValue('check_package_count', false)
    }
  }, [formik.values.parcels, totalPackageCount])
  // END: validate Total Package Count

  const totalPackages = useMemo(() => {
    let sum = 0
    Object.entries(formik.values.parcels).forEach(([key, value]: [any, any]) => {
      sum += Number.parseFloat(value?.package_count) || 0
    })
    return sum
  }, [formik.values.parcels])

  // TODO: [N] re check
  // useEffect(() => {
  //   if (hasGetPackage) {
  //     getPackages()
  //   }

  //   return () => {}
  // }, [getPackages])

  const optionsPackageTypes = useMemo(() => {
    if (isEmpty(carriersPackage)) return [OPTION_PACKAGE_TYPE.CARRIER_STANDARD_PACKAGE]

    const optionsCarriersPackage = carriersPackage.map((parent) => {
      return {
        label: parent.label,
        options: parent?.value.map((item: any) => {
          return {
            label: item.label,
            value: {
              length: item.parcel.length,
              width: item.parcel.width,
              height: item.parcel.height,
              dimension_unit: item.parcel.dimension_unit,
              weight_limit: item.parcel.weight_limit,
              package_type: item.value,
              package_id: null,
              package_count: item?.package_count || 1,
            },
          }
        }),
      }
    })

    return optionsCarriersPackage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carriersPackage])
  // END: Options service and package

  // TODO: [N] use for VNP #434
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const addPackage = () => {
    handleAddNewItem('parcels')
  }

  const removePackage = (key) => {
    handleDeleteItem('parcels', key)
  }

  const orderHelper = OrderHelper()
  const onChangeDimensionUnit = (e: any) => {
    setCurrentDimensionUnit(e.value)

    // Set dimension_unit for all parcels
    const parcels = formik.getFieldProps('parcels').value
    const firstParcelKey = Object.keys(parcels)?.[0]
    Object.keys(parcels).forEach((key) => {
      formik.setFieldValue(`parcels.${key}.dimension_unit`, e.value)
    })

    const firstParcel = parcels[firstParcelKey]
    const {package_type} = firstParcel || {}

    let {editable, fieldsHidden, packageType} = orderHelper.getPackageDetails(package_type)

    if (editable && fieldsHidden && packageType) {
      let fieldsCheck = ['length', 'width', 'height']
      fieldsCheck.forEach((field) => {
        if (fieldsHidden.includes(field)) {
          let baseValue = packageType?.parcel?.[field]
          const updatedValue = convertUnit(
            {unit: DIMENSION_UNIT.IN, value: baseValue},
            {unit: e.value}
          ).toString()
          formik.setFieldValue(`parcels.${firstParcelKey}.${field}`, updatedValue)
        }
      })
    }
  }

  const onChangeWeightUnit = (e: any) => {
    setCurrentWeightUnit(e.value)

    // Set weight_unit for all parcels
    const parcels = formik.getFieldProps('parcels').value
    Object.keys(parcels).forEach((key) => {
      formik.setFieldValue(`parcels.${key}.weight_unit`, e.value)
    })
  }

  useEffect(() => {
    const total = Object.keys(formik.values?.parcels || {}).length
    const limitRow = UNIT_VALUES.PACKAGE_COUNT.limit_row

    if (Number(total) >= Number(limitRow)) {
      setReachLimitRow(true)
    } else {
      setReachLimitRow(false)
    }
  }, [formik.values?.parcels])

  return (
    <div
      className={clsx({
        [className]: !!className,
      })}
    >
      <div>
        <div className='d-flex flex-wrap gap-2 justify-content-between align-items-center my-3'>
          <div className='fs-3 fw-bolder'>{intl.formatMessage({id: 'PACKAGE_INFO'})}</div>
        </div>

        <div className='d-flex flex-wrap gap-2 justify-content-between align-items-center mb-7'>
          <div className='d-flex col-6 col-sm-6 pe-5'>
            <PackageType
              data={[OPTION_PACKAGE_TYPE.INPUT_DIMENSION, ...cloneDeep(optionsPackageTypes)]}
              className='w-100'
              labelClassName='col-form-label fw-bolder'
              formik={formik}
              name='parcels.0.package_type'
              placeholder={packageTypePlaceholder}
              disabled={disabled || Object.keys(formik.getFieldProps('parcels').value).length > 1}
              labelPackageType={labelPackageType}
              setLabelPackageType={setLabelPackageType}
            />
          </div>
          {/* <div
            className={clsx(
              'd-flex justify-content-end align-items-center text-primary fw-bolder',
              {'cursor-pointer': !disabled}
            )}
          >
            <span
              className={clsx('d-flex justify-content-end align-items-center', {
                'pe-none opacity-50': disabled || reachLimitRow,
              })}
              onClick={() => !disabled && !reachLimitRow && addPackage()}
            >
              <span className='fs-2 me-1'>+</span>
              {intl.formatMessage({id: 'ADD_PACKAGE'})}
            </span>
          </div> */}
        </div>

        <div className={'d-flex row package-list bg-light rounded py-3 mx-0'}>
          <div className='d-flex justify-content-center align-items-center mb-1'>
            <div className='col-12 d-flex flex-wrap package-labels'>
              <div className='col-6 d-flex justify-content-between align-items-center pe-5'>
                <div className='fs-5 fw-bold ps-2 label'>
                  {intl.formatMessage({id: 'DIMENSIONS'})}{' '}
                  <span className='fw-light'>{'L*W*H'}</span>
                </div>
                <SelectFormik
                  className={clsx('dimension-unit-select', {'pe-none': disabled})}
                  placeholder=''
                  label={''}
                  labelClassName={'text-muted'}
                  options={OPTION_DIMENSION_UNIT}
                  name={`dimension_unit`}
                  formik={formik}
                  disabled={isDisabledDimension}
                  emptyDefault={false}
                  onChange={(e) => onChangeDimensionUnit(e)}
                  hideIndicatorSeparator
                  isSearchable={false}
                  {...customSelectInputStyle(isDisabledDimension)}
                />
              </div>
              <div className='col-6 d-flex justify-content-between align-items-center'>
                <div className='col-6 d-flex justify-content-between align-items-center pe-2'>
                  <div className='fs-5 fw-bold label'>{intl.formatMessage({id: 'WEIGHT'})}</div>
                  <SelectFormik
                    className={clsx('weight-unit-select', {'pe-none': disabled})}
                    label={''}
                    placeholder=''
                    labelClassName={'text-muted  bg-light'}
                    options={OPTION_WEIGHT_UNIT}
                    name={`weight_unit`}
                    formik={formik}
                    disabled={disabled}
                    emptyDefault={false}
                    onChange={(e) => onChangeWeightUnit(e)}
                    hideIndicatorSeparator
                    isSearchable={false}
                    {...customSelectInputStyle(disabled)}
                  />
                </div>
                <div className='col-6 ps-2'>
                  <div className='fs-5 fw-bold label d-flex justify-content-start align-items-center'>
                    {intl.formatMessage({id: 'PACKAGE_COUNT'})}
                    <OverlayTrigger
                      overlay={
                        <Tooltip id='tooltip-package-count'>
                          {intl.formatMessage({
                            id: 'PACKAGE_COUNT_TOOLTIP',
                          })}
                        </Tooltip>
                      }
                    >
                      <span className='d-flex justify-content-start align-items-start'>
                        <KTSVG
                          path='/media/gori/common/info.svg'
                          className='ms-1 mb-1 svg-icon-4 cursor-pointer'
                        />
                      </span>
                    </OverlayTrigger>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {Object.entries(formik.getFieldProps('parcels').value).map(([keyItem], index) => {
            const parcels = formik.getFieldProps('parcels').value
            const firstParcel = Object.values(parcels)?.[0] as ParcelItem
            const packageType = firstParcel.package_type
            const detectPackageType = carriersPackage
              ?.flatMap((carrier: any) =>
                carrier.value.filter((item: any) => item.value === packageType)
              )
              .find(Boolean)

            const editable = detectPackageType?.parcel?.editable

            const values = Object.entries(formik.getFieldProps('parcels').value)
            const disabledDelete = values.length < 2 || disabled
            const displayExtendParts = values.length === 1 && index === 0

            return (
              <div key={keyItem} className={'package-item my-1'}>
                <div className='row'>
                  <div className='col-12 d-flex flex-wrap'>
                    <div className='col-6 col sm-6 pe-3 d-flex'>
                      <div
                        className={clsx(
                          'col-1 text-muted m-0 fw-normal text-end d-flex justify-content-center align-self-start pt-4',
                          {
                            'd-none': displayExtendParts,
                          }
                        )}
                      >
                        {index + 1}.
                      </div>
                      <div className={displayExtendParts ? 'col-12' : 'col-11'}>
                        <div className='dimensions-wrap d-flex justify-content-start'>
                          {DIMENSIONS.map((item, index) => {
                            let isFieldHidden =
                              editable && detectPackageType?.parcel?.hidden.includes(item)

                            return (
                              <div
                                className={clsx('col-4 col-sm-4 pe-2', {'d-none': isFieldHidden})}
                                key={item || index}
                              >
                                <div className='position-relative dimension-input-wrap'>
                                  <InputTextFormik
                                    type='number'
                                    min={0}
                                    className={clsx('border-0', {'pe-none': isDisabledDimension})}
                                    labelClassName={'text-muted'}
                                    label={''}
                                    formik={formik}
                                    onChange={() => {
                                      if (labelPackageType?.toLowerCase().includes('cubic')) return

                                      formik.setFieldValue(`parcels.${keyItem}.package_type`, null)
                                      formik.setFieldValue(`parcels.${keyItem}.package_id`, null)
                                    }}
                                    name={`parcels.${keyItem}.${item}`}
                                    disabled={isDisabledDimension}
                                    inputClassName={clsx('dimension-input rounded')}
                                    configAppend={{
                                      name: DIMENSIONS_PLACEHOLDER[item],
                                      position: 'left',
                                    }}
                                  />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                    <div className='col-3 col-sm-3 pe-3'>
                      <div className={'weight-wrap'}>
                        <InputTextFormik
                          type='number'
                          min={0}
                          className={clsx('', {'pe-none': disabled})}
                          labelClassName={'text-muted'}
                          label={''}
                          formik={formik}
                          name={`parcels.${keyItem}.weight`}
                          disabled={disabled}
                        />
                      </div>
                    </div>
                    <div className='col-3 col-sm-3 ps-2'>
                      <div className='col-12 d-flex justify-content-center align-items-center'>
                        <div className={displayExtendParts ? 'col-12' : 'col-10'}>
                          <InputTextFormik
                            type='number'
                            min={0}
                            className={clsx('border-0', {'pe-none': isDisabledPackageCount})}
                            labelClassName={'text-muted'}
                            label={''}
                            formik={formik}
                            name={`parcels.${keyItem}.package_count`}
                            disabled={isDisabledPackageCount}
                            isInteger={true}
                          />
                        </div>
                        <div
                          className={clsx('col-2 pt-2 align-self-start', {
                            'd-none': displayExtendParts,
                          })}
                        >
                          <Button
                            event={() => removePackage(keyItem)}
                            className={clsx('text-hover-danger border-0 bg-transparent', {
                              'pe-none opacity-50': disabledDelete,
                            })}
                            disabled={disabledDelete}
                          >
                            <span className='fs-4 text-gray-600'>
                              <KTSVG path='/media/gori/orders/trash.svg' className={'svg-icon-1'} />
                            </span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className='row'>
          <div className='col-12 d-flex flex-wrap'>
            <div className='col-6 d-flex flex-column justify-content-center align-items-start'>
              <div className='d-flex align-items-center text-muted mt-3'>
                {intl.formatMessage({id: 'TOTAL_WEIGHT'})}:
                <input name='check_weight' type='hidden' />
                <span className='text-dark fs-1 mx-2'>{totalWeightPackage}</span>{' '}
                {currentWeightUnit?.toUpperCase()}
              </div>
              {formik.errors?.check_weight && (
                <div className='text-danger d-flex justify-content-end ps-0'>
                  {formik.errors?.check_weight}
                </div>
              )}
            </div>
            <div className='col-6 d-flex flex-column justify-content-center align-items-end'>
              <div className='d-flex align-items-center text-muted mt-3'>
                {intl.formatMessage({id: 'TOTAL_PACKAGES'})}:
                <input name='check_weight' type='hidden' />
                <span className='text-dark fs-1 mx-2'>{totalPackages}</span>
              </div>
              {formik.errors?.check_package_count && (
                <div className='text-danger d-flex justify-content-end ps-0'>
                  {formik.errors?.check_package_count}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export {PackageInfo}
